/**
 * Geometry for **the rail**: a travelling hand drawn as a notched bar laid
 * on the keys it visits.
 *
 * This is the second grammar of the S0 keyboards. A hand that has a
 * *placement* gets lit keys, because its notes fit under five fingers at
 * once and "put your fingers here" is a true sentence about them. A hand
 * that travels gets this instead: lighting its keys would assert a hand
 * shape that existed at no single instant. `chordDrawing` decides which.
 *
 * ## Why a rail and not a time × pitch path
 *
 * The previous drawing was a polyline, time down, pitch across. It failed
 * for a reason that is structural, not cosmetic: the vertical axis offers
 * 44 usable viewBox units (56.6px) for up to 8 onsets — 8.1px between two
 * notes — while the horizontal axis offers 504px and melodic leaps span 20
 * to 60px. Every segment came out near-horizontal, and the slightest
 * descent crossed the segment before it. On the real corpus the median
 * trace holds 7 points, 39.6% hold 10 or more, 25.6% reverse direction at
 * least 4 times; the worst is 21 points, 12 reversals, 38 semitones wide.
 *
 * The rail drops the time axis and keeps only *which keys, in what order*.
 * Two properties follow, and they are the whole argument:
 *
 * - the notch count is bounded by the number of **distinct keys**, not by
 *   the number of attacks — 21 attacks across 12 semitones yield at most
 *   13 notches;
 * - there are no segments, so nothing can cross anything.
 *
 * Order survives as brightness (dim at the start, bright at the end) plus
 * two markers: a hollow ring where the line starts, a filled dot where it
 * ends. Rhythm does not survive, and is not meant to: it is the piano roll's
 * job, two centimetres below.
 *
 * Geometry comes from `MiniPiano` itself (`keyCenterX`). Computing a midi→x
 * mapping here would have to be linear in pitch, and a keyboard is not:
 * white keys are evenly spaced, black ones offset between them. The two
 * would land on different keys, which is precisely what an overlay exposes.
 * `__tests__/mini-piano-geometry.test.ts` locks that contract.
 */

import {
  KEYBOARD_VIEWBOX_HEIGHT,
  keyCenterX,
} from "@/components/ui/molecules/mini-piano";
import type { PhraseNote } from "@/lib/learning-journey/types";

// ── Vertical placement ────────────────────────────────────────────

/**
 * Baseline of a lone rail, in viewBox units.
 *
 * The keyboard is 56 units tall, black keys stop at 34.7, and the note
 * labels sit around 47-53. 41 is the only band that is white-key-only,
 * clear of the black keys above and of the labels below.
 */
export const PITCH_RAIL_Y = 41;

/**
 * Baselines when both hands travel and each needs its own lane.
 *
 * These used to be 36 and 45, nine units apart, sized against the focus
 * block alone. In a frieze cell nine units are 3.4 to 6.4px, and the rails
 * are painted one after the other, so the upper one's casing landed on the
 * lower one's core: at a 21px cell the two colours touched with no dark
 * between them, and at 16px the lower hand was down to a single row of
 * pixels. Two hands read as one bicolour bar. That case used to be rare
 * enough to arbitrate away; it is now a fifth of the two-handed windows.
 *
 * The pair therefore spreads to the two edges of what the keyboard offers,
 * 16 units apart:
 *
 * - *upward*, 34 sits just inside the black keys, which stop at 34.7 and
 *   are as dark as the casing, so the drawing loses nothing by meeting them;
 * - *downward*, 50 reaches into the band the note labels use — and that is
 *   safe by construction, not by luck. Two lanes mean neither hand has a
 *   placement, so `chordDrawing` lights no key at all, and `MiniPiano` only
 *   ever labels a key it has lit.
 *
 * 16 units is what the smallest keyboard can pay for: 4.6px at the 16px
 * floor `keyboardGeometry` allows, against the 3px endpoint of `sm-pair`.
 * `__tests__/learn-piece/pitch-rail.test.ts` holds that arithmetic.
 */
const PITCH_RAIL_Y_UPPER = 34;
const PITCH_RAIL_Y_LOWER = 50;

/**
 * Where a hand's rail sits, in viewBox units.
 *
 * With a single travelling hand there is one lane and it takes the clear
 * band. With both hands travelling the two rails must never stack on the
 * same line — superimposed, they would read as one rail of ambiguous colour
 * — so they split into two lanes.
 *
 * The right hand takes the upper lane and the left hand the lower one,
 * mirroring the grand staff: the left hand is the one written underneath.
 */
export function pitchRailY(
  hand: "left" | "right",
  laneCount: number,
): number {
  if (laneCount < 2) {
    return PITCH_RAIL_Y;
  }

  return hand === "left" ? PITCH_RAIL_Y_LOWER : PITCH_RAIL_Y_UPPER;
}

// ── Sizes ─────────────────────────────────────────────────────────

/**
 * Which set of stroke widths a rail is drawn with.
 *
 * `lg` on the focus keyboard, `sm` in a frieze cell — and `sm-pair` in the
 * shortest frieze cells when they have to hold *two* rails. That last one
 * is not a third scale: it is `sm` narrowed until two lanes fit one above
 * the other on a keyboard that can be 16px tall. Widening the lanes is what
 * fixes the pair everywhere else; below a point it stops being enough,
 * because two lanes together cannot be thicker than the keyboard.
 */
export type PitchRailVariant = "lg" | "sm" | "sm-pair";

/**
 * Stroke widths, in **screen pixels**: every stroke of the rail carries
 * `vector-effect: non-scaling-stroke`, so the same object holds together at
 * 40px and at 144px tall without being respecified. This is a pixel
 * setting, never a threshold of representation — nothing here may make the
 * rail disappear, because a player would read "no drawing" as "this hand
 * does not play".
 */
export type PitchRailMetrics = {
  /** Dark casing under the rail, so it reads on white and black keys alike. */
  readonly railCasing: number;
  readonly rail: number;
  readonly notch: number;
  readonly endpointCasing: number;
  readonly endpoint: number;
  /** Punched out of the start dot to turn it into a ring. */
  readonly startHole: number;
};

/**
 * ## How these numbers were chosen
 *
 * The reference is the window 84.7% of the corpus actually gets: four
 * octaves, a 392-unit viewBox drawn across the 504px panel, so **1 unit =
 * 1.286px and a white key is 18px**. Everything below is read against that
 * key width, because that is the object the player is trying to identify.
 *
 * - `notch` at 8px is 44% of a white key — a mark, not a speck. Its ceiling
 *   is the tightest chromatic spacing, C→C♯ = 7 units = 9px at this scale:
 *   past that, two neighbouring semitones fuse into one blob and the rail
 *   stops answering "which keys".
 * - the casings keep the ~1.5px of dark on each side they had before. That
 *   margin is the entire reason the rail survives crossing from a white key
 *   to the white gap under a black one, and it has to grow with what it
 *   surrounds or it stops being a contrast device.
 * - `endpoint` is 1.4× the notch, not the 1.5× it used to be. The endpoints
 *   carry the reading order, but the ring-versus-disc contrast does most of
 *   that work; every extra pixel of endpoint is paid for twice in the
 *   two-lane case, where the drawing is tallest (see `pitchRailY`).
 *
 * `sm` is not `lg` scaled down. A frieze cell is 16-40px tall, so the same
 * pixel width covers three to five times more of the drawing; the numbers
 * are held near the floor of legibility on purpose, and what they buy at
 * that size is presence, not identification of individual keys.
 *
 * `sm-pair` is `sm` taken down to ~55%, the ratio that makes two lanes fit
 * in the cells too short for `sm` to hold them (see `pitchRailVariant`).
 * The budget is set by the 16px cell: the two lanes are 16 units apart,
 * 4.6px there, and the rails are painted in order, so the upper one's
 * casing lands on the lower one's core. Half an endpoint plus half its
 * casing — 3.75px at these numbers — has to fit inside that 4.6px, and what
 * is left over, 1.6px, is the dark that says "two hands". Anything larger
 * and the lower hand starts losing ink, which is the one thing the rail may
 * never do.
 *
 * The proportions of `sm` survive the shrink, because they are what carries
 * the reading: endpoint ≈ 1.4 × notch so an end is not read as a step, and
 * a hole at 40% of the endpoint so the start still reads as hollow. Only
 * `rail` is held above the ratio, at 2px: it is the one stroke with no
 * shape to lose, and under two pixels a line stops being a line.
 */
export const PITCH_RAIL_METRICS: Readonly<
  Record<PitchRailVariant, PitchRailMetrics>
> = {
  lg: {
    railCasing: 11,
    rail: 5,
    notch: 8,
    endpointCasing: 13,
    endpoint: 11,
    startHole: 4.5,
  },
  sm: {
    railCasing: 7,
    rail: 3,
    notch: 4,
    endpointCasing: 7.5,
    endpoint: 5.5,
    startHole: 2.2,
  },
  "sm-pair": {
    railCasing: 4,
    rail: 2,
    notch: 2.2,
    endpointCasing: 4.5,
    endpoint: 3,
    startHole: 1.2,
  },
};

/**
 * Shortest keyboard on which two lanes still fit the `sm` widths.
 *
 * The lower lane sits at 50 of 56, so the bottom half of its 5.5px arrival
 * disc has 6 viewBox units to live in — and 6 units are worth 5.5px only
 * from 25.7px of keyboard up. Below that the disc is cut by the edge of the
 * drawing, and on the way the dark between the two colours drops under 2px.
 * Rounded up to the pixel.
 */
const SM_TWO_LANE_MIN_PX = 26;

/**
 * Which widths a rail gets on a keyboard of `heightPx` carrying `laneCount`
 * rails.
 *
 * The size threshold is the viewBox height: past it the keyboard is drawn
 * at 1:1 or larger, and the heavier strokes stop covering the keys.
 *
 * The lane count only narrows the *short* small keyboards. `lg` has room
 * for two lanes as it stands — the focus keyboard is 72px at its worst,
 * where the 16-unit gap is 20.6px against an 11px endpoint — and so does
 * most of the frieze. Narrowing where it is not needed is not free: the
 * pair widths cost the notches and the start ring most of their presence,
 * which is exactly what a 40px cell has the room to keep.
 */
export function pitchRailVariant(
  heightPx: number,
  laneCount: number,
): PitchRailVariant {
  if (heightPx >= KEYBOARD_VIEWBOX_HEIGHT) {
    return "lg";
  }

  if (laneCount >= 2 && heightPx < SM_TWO_LANE_MIN_PX) {
    return "sm-pair";
  }

  return "sm";
}

/** Ink of the casings and of the hole in the start ring. */
export const PITCH_RAIL_CASING_COLOR = "#0d0d12";

/** Opacity of the rail's core, under the notches it carries. */
export const PITCH_RAIL_OPACITY = 0.22;

// ── Model ─────────────────────────────────────────────────────────

/** One notch: a distinct key the hand visited. */
export type PitchRailNotch = {
  readonly midi: number;
  /** Centre of the key, in viewBox units. */
  readonly x: number;
  /** `0.4` on the first key visited, `1` on the last. */
  readonly opacity: number;
};

export type PitchRailGeometry = {
  /** Rail extremities, in viewBox units. */
  readonly x1: number;
  readonly x2: number;
  /** One per distinct key, in order of first visit. */
  readonly notches: readonly PitchRailNotch[];
  /** Key the line starts on. */
  readonly startX: number;
  /** Key the line ends on. May equal `startX`. */
  readonly endX: number;
};

/** Rail overhang past the outermost key centre, in viewBox units. */
const OVERHANG = 3;

/** Dimmest a notch gets, so the first key stays plainly visible. */
const MIN_OPACITY = 0.4;

/**
 * Build the rail for one hand's notes.
 *
 * `notes` must be in **chronological order** — `PhraseChordHand.notes`
 * guarantees it — since the order is the only thing left encoding time:
 * it decides each key's brightness and which end is the arrival.
 *
 * Notes outside the keyboard window are dropped, never folded onto the
 * edge key: a rail that lands on a key the hand never touched is worse
 * than a rail that is one notch short. Returns `null` when nothing is left
 * to draw.
 */
export function buildPitchRail(
  notes: readonly PhraseNote[],
  lowMidi: number,
  octaves: number,
): PitchRailGeometry | null {
  const seen = new Set<number>();
  const visits: { readonly midi: number; readonly x: number }[] = [];
  let startX: number | null = null;
  let endX = 0;

  for (const note of notes) {
    const x = keyCenterX(note.midi, lowMidi, octaves);

    if (x === null) {
      continue;
    }

    if (!seen.has(note.midi)) {
      seen.add(note.midi);
      visits.push({ midi: note.midi, x });
    }

    if (startX === null) {
      startX = x;
    }

    endX = x;
  }

  if (startX === null || visits.length === 0) {
    return null;
  }

  const last = visits.length - 1;
  const notches = visits.map((visit, rank) => ({
    midi: visit.midi,
    x: visit.x,
    // A single key has no progression to express, and `rank / 0` is not a
    // number: it simply reads at full strength.
    opacity:
      last === 0 ? 1 : MIN_OPACITY + (1 - MIN_OPACITY) * (rank / last),
  }));
  const xs = visits.map((visit) => visit.x);

  return {
    x1: Math.min(...xs) - OVERHANG,
    x2: Math.max(...xs) + OVERHANG,
    notches,
    startX,
    endX,
  };
}
